import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Ball {
  constructor(scene, physicsWorld) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    
    // Ball properties updated to match Rocket League specs
    this.radius = 2.0;
    this.initialPosition = { x: 0, y: 5, z: 0 };
    this.isFrozen = false;
    this.maxSpeed = 60; // 6000uu/s as per plan.mdc
    this.lastHitTime = 0; // Track last hit time for power shots
    this.powerHitCooldown = 1000; // Cooldown for power hits in milliseconds
    
    // Create mesh and physics body
    this.createBallMesh();
    this.createBallBody();
    
    // Add to scene
    this.scene.add(this.mesh);
  }
  
  createBallMesh() {
    // Create ball geometry and material
    const ballGeometry = new THREE.SphereGeometry(this.radius, 32, 32);
    const ballMaterial = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      shininess: 30
    });
    
    // Create mesh
    this.mesh = new THREE.Mesh(ballGeometry, ballMaterial);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    
    // Add hexagon pattern to the ball
    this.addHexagonPattern();
    
    // Position the ball
    this.mesh.position.set(
      this.initialPosition.x,
      this.initialPosition.y,
      this.initialPosition.z
    );
  }
  
  addHexagonPattern() {
    // Create a simple hexagon pattern for the ball using a texture
    const textureSize = 512;
    const canvas = document.createElement('canvas');
    canvas.width = textureSize;
    canvas.height = textureSize;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, textureSize, textureSize);
    
    // Draw hexagons
    ctx.fillStyle = 'black';
    const hexSize = textureSize / 8;
    
    // Simple pattern of hexagons
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        if ((row + col) % 2 === 0) {
          ctx.beginPath();
          ctx.arc(
            col * hexSize + hexSize / 2,
            row * hexSize + hexSize / 2,
            hexSize / 3,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
      }
    }
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    this.mesh.material.map = texture;
    this.mesh.material.needsUpdate = true;
  }
  
  createBallBody() {
    // Create sphere physics body with adjusted properties
    this.body = this.physicsWorld.createSphereBody(this.radius, this.initialPosition, {
      mass: 30,  // Increased mass for more stability
      material: this.physicsWorld.ballMaterial
    });
    
    // Configure physics properties
    this.body.linearDamping = 0.4;  // Increased damping to prevent bouncing
    this.body.angularDamping = 0.6; // Increased angular damping for more stability
    
    // Set restitution for bouncing
    this.body.restitution = 0.2;  // Reduced bounciness further
    
    // Add sleep thresholds for better performance
    this.body.sleepSpeedLimit = 0.5; // Increased sleep threshold
    this.body.sleepTimeLimit = 0.5;  // Reduced sleep time
    
    // Ensure the ball stays awake
    this.body.allowSleep = false;
  }
  
  freeze() {
    this.isFrozen = true;
    // Store current velocity and angular velocity
    this.savedVelocity = this.body.velocity.clone();
    this.savedAngularVelocity = this.body.angularVelocity.clone();
    
    // Set velocities to zero
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    
    // Make the body static
    this.body.type = CANNON.Body.STATIC;
  }
  
  unfreeze() {
    this.isFrozen = false;
    // Make the body dynamic again
    this.body.type = CANNON.Body.DYNAMIC;
    
    // Optionally restore previous velocity
    if (this.savedVelocity) {
      this.body.velocity.copy(this.savedVelocity);
      this.body.angularVelocity.copy(this.savedAngularVelocity);
    }
  }
  
  hitByPlayer(carVelocity, contactPoint) {
    // Calculate how long since the last hit
    const now = Date.now();
    const timeSinceLastHit = now - this.lastHitTime;
    
    // Get current ball velocity
    const ballVel = this.body.velocity;
    
    // Calculate base hit power based on car velocity magnitude
    const carSpeed = new THREE.Vector3(carVelocity.x, carVelocity.y, carVelocity.z).length();
    let hitPower = Math.min(carSpeed * 1.5, 50); // Scale car speed with a cap
    
    // Power shot bonus for fast consecutive hits - more reasonable 25% boost (was 50%)
    if (timeSinceLastHit < this.powerHitCooldown && timeSinceLastHit > 100) {
      // If hit in quick succession but not too quick (avoid double counting)
      hitPower *= 1.25; // 25% boost to power (was 1.5)
      console.log("Power shot triggered!");
    }
    
    // Direction vector from point of contact to ball center - Rocket League style impact normal
    const hitDirection = new THREE.Vector3();
    hitDirection.subVectors(
      new THREE.Vector3(this.body.position.x, this.body.position.y, this.body.position.z),
      new THREE.Vector3(contactPoint.x, contactPoint.y, contactPoint.z)
    ).normalize();
    
    // Apply impulse to the ball
    const impulse = new CANNON.Vec3(
      hitDirection.x * hitPower,
      hitDirection.y * hitPower + hitPower * 0.2, // Slight upward angle for better shots
      hitDirection.z * hitPower
    );
    
    this.body.applyImpulse(impulse, new CANNON.Vec3(contactPoint.x, contactPoint.y, contactPoint.z));
    
    // Update last hit time
    this.lastHitTime = now;
  }
  
  update() {
    // Update mesh position and rotation from physics body
    this.mesh.position.copy(this.body.position);
    this.mesh.quaternion.copy(this.body.quaternion);
    
    // Ensure ball stays within the stadium bounds
    this.keepBallInStadium();
    
    // Cap ball velocity to match Rocket League specs
    this.capBallVelocity();
  }
  
  capBallVelocity() {
    // Get current velocity
    const velocity = this.body.velocity;
    const speed = velocity.length();
    
    // Cap to maximum speed (60 = 6000uu/s per plan.mdc)
    if (speed > this.maxSpeed) {
      // Scale velocity to max speed
      const scale = this.maxSpeed / speed;
      velocity.scale(scale, velocity);
      this.body.velocity.copy(velocity);
    }
  }
  
  keepBallInStadium() {
    // Updated stadium dimensions
    const maxWidth = 100;
    const maxLength = 160;
    const maxHeight = 30;
    
    const pos = this.body.position;
    let needsReset = false;
    
    // Check if ball went outside stadium bounds (with some margin)
    // Don't reset near the goals to allow scoring
    if ((Math.abs(pos.x) > maxLength/2 + 15 && Math.abs(pos.z) > maxWidth/4) || 
        Math.abs(pos.z) > maxWidth/2 + 15 || 
        pos.y > maxHeight + 15 || 
        pos.y < -10) {
      needsReset = true;
    }
    
    // If ball escaped, reset it to center
    if (needsReset) {
      console.log("Ball went out of bounds - resetting");
      this.reset();
    }
    
    // Ground check and damping
    if (pos.y < this.radius + 0.1) { // If ball is near ground
      // Apply stronger damping to vertical velocity
      this.body.velocity.y *= 0.8;
      
      // If moving very slowly vertically, force it to stop bouncing
      if (Math.abs(this.body.velocity.y) < 1) {
        this.body.velocity.y = 0;
        this.body.position.y = this.radius;
      }
    }
    
    // Dampen extreme velocities to prevent erratic behavior
    const maxVelocity = 25;
    const velocity = this.body.velocity;
    const speed = velocity.length();
    
    if (speed > maxVelocity) {
      // Scale down velocity to maximum
      velocity.scale(maxVelocity / speed, velocity);
      this.body.velocity.copy(velocity);
    }
    
    // Apply additional damping if the ball is rolling very slowly
    if (speed < 2 && pos.y < this.radius + 0.5) {
      // If ball is moving very slowly near the ground, slow it down more
      this.body.velocity.scale(0.95, this.body.velocity);
      this.body.angularVelocity.scale(0.95, this.body.angularVelocity);
      
      // If extremely slow, stop completely to prevent infinite rolling
      if (speed < 0.5) {
        this.body.velocity.scale(0.8, this.body.velocity);
        this.body.angularVelocity.scale(0.8, this.body.angularVelocity);
      }
    }
  }
  
  getPosition() {
    return this.body.position;
  }
  
  reset() {
    // Reset position and velocity
    this.body.position.set(
      this.initialPosition.x,
      this.initialPosition.y,
      this.initialPosition.z
    );
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    
    // CRITICAL: Make sure ball is unfrozen
    this.isFrozen = false;
    this.body.type = CANNON.Body.DYNAMIC;
    
    // Force the body to wake up
    this.body.wakeUp();
    
    // Make sure mesh follows physics body
    this.mesh.position.copy(this.body.position);
    this.mesh.quaternion.copy(this.body.quaternion);
    
    console.log("Ball reset to center position, dynamic state confirmed");
  }
} 